/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Pencil, PlusCircle } from 'react-bootstrap-icons';
import qs from 'query-string';
import mapValues from 'lodash/mapValues';

import { axios } from 'api';
import { useAppNavigate } from 'hooks';
import { IDynamicAction } from 'plugins/api/types';
import { useCallCommand } from 'plugins/api';
import { loadDataUrlParser, paramsToPathParser, paramsToUrlParser } from 'plugins/util/parser';
import * as Components from 'components/containers';
import { DynamicPage } from 'plugins/pages';

import { Button, Modal } from 'components/controls';
import { IWiseTableAction } from 'components/wise';

import { TrashIcon } from 'assets/icons';

import styles from 'theme/pages/Clients/Clients.module.scss';

// typ danych wejściowych
interface IProps {
  action: IDynamicAction | IWiseTableAction;
  refetch: () => void;
  item: any;
}

type IComponentKeys = 'ClientForm' | 'Order';
type IParamsList = { id?: string; mode?: string };

const MainActon: FC<IProps> = ({ action, refetch, item }) => {
  const { search } = useLocation();
  const navigate = useAppNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const confirmMessage =
    (action.method_params as { confirm_message?: string } | undefined)?.confirm_message ||
    t('Czy na pewno usunąć ten element?');

  const handleDeleteConfirmed = () => {
    if (isDeleting) return;
    const rawUrl = action.method_params?.command_url || '';
    const deleteUrl = rawUrl.includes(':')
      ? rawUrl.replace(/:([a-zA-Z_]+)/g, (_, key: string) => item[key] ?? '')
      : loadDataUrlParser(rawUrl, qs.parse(search)) || rawUrl;

    setIsDeleting(true);
    axios
      .delete(deleteUrl)
      .then(() => {
        setConfirmDeleteOpen(false);
        if (action.refresh_data_mode === 'ONSUCCESS') refetch();
      })
      .catch(() => undefined)
      .finally(() => setIsDeleting(false));
  };

  // import komponentu po nazwie w definicji akcji
  const componentKey = action?.method_params?.component_name;
  const Component =
    typeof componentKey === 'string' ? Components[componentKey as IComponentKeys] : null;

  // lista parametrów
  const { id, mode, ...restParams }: IParamsList = qs.parse(
    action.method_params?.params_list?.replaceAll(';', '&') || ''
  );

  // poprawenie wartości parametru id
  const propsId = item[id?.replace(':', '') || ''];

  // lista propsów
  const paramsProps = mode === 'EDIT' ? { id: parseInt(propsId) } : {};

  // otrzymywanie endpointu do metody callCommand
  const callCommandUrl = loadDataUrlParser(
    action.method_params?.command_url || '',
    qs.parse(search)
  );

  // wywołanie metody CallCommand
  const { mutate: callCommand } = useCallCommand(callCommandUrl || '');

  // parsowanie parametrów do obiektu
  const paramsObject = mapValues(
    restParams,
    (param: string) => item[param?.replace(':', '') || '']
  );

  // parsowanie parametrów do urla
  const paramsUrl = qs.stringify({ ...paramsObject, mode });

  const handleButtonClick = () => {
    if (action.method === 'OpenModalComponent' || action.method === 'OpenModalDynamicPage') {
      setOpen(true);
    }

    if (action.method === 'Delete') {
      setConfirmDeleteOpen(true);
    }

    if (action.method === 'Redirect') {
      if (
        action.type === 'CONTEXT_MAIN' ||
        action.type === 'CONTEXT_ADDITIONAL' ||
        action.type === 'COLUMN'
      ) {
        const pageUrl = action.method_params?.page_url;
        if (!pageUrl) return;
        const redirectMode = (action.method_params as { redirect_mode?: 'query' | 'path' })
          ?.redirect_mode;
        if (redirectMode === 'path' && action.method_params?.params_list) {
          const pathSegment = paramsToPathParser(action.method_params.params_list, item);
          navigate(pathSegment ? `${pageUrl}/${pathSegment}` : pageUrl);
        } else {
          navigate(pageUrl + (paramsUrl ? `?${paramsUrl}` : ''));
        }
      }
    }

    if (action.method === 'CallCommand') {
      callCommand(paramsObject);
    }
  };

  const handleSuccess = () => {
    setOpen(false);

    if (action.refresh_data_mode === 'ONSUCCESS') {
      refetch();
    }
  };

  const renderButtonIcon = () => {
    // Sprawdź czy akcja ma customową ikonę
    const actionWithCustomIcon = action as IDynamicAction & { customIcon?: React.ReactNode };
    
    if (actionWithCustomIcon.customIcon) {
      return actionWithCustomIcon.customIcon;
    }

    // Domyślne ikony
    if (action.style === 'ACTION_EDIT') {
      return <Pencil />;
    }

    if (action.style === 'DELETE') {
      return <TrashIcon />;
    }

    if (action.style === 'ACTION_PLUS') {
      return <PlusCircle />;
    }

    return null;
  };

  const renderModalContent = () => {
    if (action.method === 'OpenModalComponent') {
      return (
        <Modal onClose={() => setOpen(false)} title="">
          {Component && (
            <Component
              onCancel={() => setOpen(false)}
              onSuccess={() => handleSuccess()}
              {...paramsProps}
            />
          )}
        </Modal>
      );
    }

    if (action.method === 'OpenModalDynamicPage') {
      const requestParams = action.method_params?.params_list
        ? qs.parse(
            paramsToUrlParser(action.method_params?.params_list, { ...item, ...qs.parse(search) })
          )
        : {};

      return (
        <Modal fullWidth onClose={() => setOpen(false)} title="">
          <DynamicPage
            pageSymbol={action.method_params?.dynamic_page + search || ''}
            componentParams={{
              fnCallback: () => setOpen(false),
              isModal: false,
              refetchFn: refetch
            }}
            requestParams={requestParams}
          />
        </Modal>
      );
    }

    return null;
  };

  return (
    <>
      <button
        onClick={() => handleButtonClick()}
        className={classnames(styles.editButton, action.style)}>
        {renderButtonIcon()}
      </button>
      {open && renderModalContent()}
      {confirmDeleteOpen && (
        <Modal
          title={t('Potwierdź usunięcie')}
          onClose={() => (isDeleting ? undefined : setConfirmDeleteOpen(false))}>
          <div style={{ padding: '8px 4px 24px', minWidth: 320 }}>{confirmMessage}</div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button
              color="gray"
              ghost
              disabled={isDeleting}
              onClick={() => setConfirmDeleteOpen(false)}>
              {t('Nie')}
            </Button>
            <Button color="danger" loading={isDeleting} onClick={handleDeleteConfirmed}>
              {t('Tak')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default MainActon;
